import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { CultivoService } from '../cultivo.service';
import { CultivoViewModel, TwCultivoViewModel, TwRecintoCultivosViewModel } from '../cultivo.types';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'app-cultivo-wrapper-edit',
    templateUrl: './cultivo-wrapper-edit.component.html',
    styleUrls: ['./cultivo-wrapper-edit.component.scss'],
})
export class CultivoWrapperEditComponent extends ComponentBase implements OnInit {
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    visibilitySpinner = false;
    cultivo: CultivoViewModel;
    recintoCultivos: TwRecintoCultivosViewModel | null;
    superficieCultivada = 0; // Contendrá la suma de las superficies ocupadas por cada cultivo del recinto.
    formGroup: FormGroup;
    readonly = false;

    provincias: Tc[];
    municipios: Tc[];
    tiposCultivo: Tc[];

    private successMessage = 'Cultivo modificado correctamente';
    private messageErrorSuperficieMaximaSuperada = 'La superficie de los cultivos es superior a la superficie total del recinto';
    private listUrl = '/campanias/recinto/cultivos';

    // El identificador del nuevo cultivo, será una letra, que se asigna al cultivo en función del número de cultivos del recinto.
    private caracteresParaIdentificadorCultivo = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idCultivo: number = parseInt('' + params.get('idCultivo'));
        if (idCultivo) {
            this.getCultivo(idCultivo); // En la edición del cultivo no necesitamos recalcular la superficie cultivada, porque solo permitiremos dar de baja el cultivo.
            return;
        }
        // Si no entramos por id cultivo, entramos por id recinto. Este caso sucede cuando el cultivo es nuevo.
        const idRecinto: number = parseInt('' + params.get('id'));
        if (idRecinto) {
            this.getRecintoCultivos(idRecinto);
        }
    });

    private activatedRouteData$ = this.activatedRoute.data.subscribe(data => {
        this.readonly = data['readonly'] === true;
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private decimalPipe: DecimalPipe,
        private cultivoService: CultivoService,
        private navMenuService: NavMenuService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            IdVariedad: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            SuperficieVariedad: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            ProduccionEstimada: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            // The following are read only but will be shown on screen.
            Grupo: [''],
            Forma: [''],
            CodigoEppo: ['', Validators.required],
            NombreCientifico: [''],
            NombreComun: [''],
            Variedad: [''],
            Identificador: [{ value: null, disabled: true }],
            // hidden
            Id: [null],
            IdRecinto: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdUsuario: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            FechaCreacion: [null],
            FechaBaja: [{ value: null, disabled: true }],
            Desautorizado: [{ value: null, disabled: true }],
        });
    }

    private getCultivo(id: number): void {
        this.visibilitySpinner = true;
        this.cultivoService
            .getCultivo(id)
            .pipe(
                tap((vm: CultivoViewModel) => {
                    this.cultivo = { ...vm };
                    this.visibilitySpinner = false;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * Obtiene el Recinto y sus cultivos, para calcular la superficie ocupada y disponible.
     * @param idRecinto number
     * @returns void
     */
    private getRecintoCultivos(idRecinto: number): void {
        if (!idRecinto) {
            return;
        }
        this.visibilitySpinner = true;
        this.cultivoService.getRecintoCultivos(idRecinto).subscribe(results => {
            this.recintoCultivos = results;
            this.visibilitySpinner = false;
            this.superficieCultivada = this.cultivoService.getSuperficieCultivada(this.recintoCultivos.TwCultivoCollection, this.cultivo?.Id);
            this.navMenuService.updateMenuServiceForRecinto(idRecinto);
        });
    }

    onSave(): void {
        if (this.formGroup.invalid) {
            return;
        }
        const formRawValue = this.formGroup.getRawValue();

        const model: CultivoViewModel = { ...formRawValue };

        const superficieCultivadaValidated = this.cultivoService.validateSuperficieCultivada(
            model.SuperficieVariedad,
            this.superficieCultivada,
            this.recintoCultivos!.SuperficieTotal,
        );
        if (!superficieCultivadaValidated) {
            const superficieOcupada = `${this.decimalPipe.transform(this.superficieCultivada + model.SuperficieVariedad, '1.2-5', 'es')}ha`;
            const superficieTotal = `${this.decimalPipe.transform(this.recintoCultivos!.SuperficieTotal, '1.2-5', 'es')}ha`;
            this.addErrorMessage(this.messageErrorSuperficieMaximaSuperada + ': ' + superficieOcupada + ' > ' + superficieTotal);
            return;
        }
        model.IdRecinto = this.getIdRecinto();
        if (!model.IdUsuario) {
            model.IdUsuario = this.authService.getLoggedInUser()?.Id || 0;
        }
        if (this.cultivo?.FechaCreacion) {
            model.FechaCreacion = this.cultivo?.FechaCreacion;
        }
        if (!model.Id) {
            model.Identificador = this.CalcularIdentificadorCultivo();
        }

        // Delete every field that does not match TwCultivoViewModel
        delete model.CodigoEppo;
        delete model.Forma;
        delete model.Grupo;
        delete model.NombreCientifico;
        delete model.NombreComun;
        delete model.Variedad;

        model.FechaBaja = this.cultivoService.checkFechaBaja(model.FechaBaja);

        this.visibilitySpinner = true;

        const payload = model as TwCultivoViewModel;

        this.cultivoService
            .save(payload)
            .pipe(
                tap(() => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.visibilitySpinner = false;
                    this.catchServerError(error);
                    if (error?.message?.indexOf(this.messageErrorSuperficieMaximaSuperada) != -1) {
                        this.formGroup.disable();
                    }
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * Se calcula el siguiente identificador del cultivo a partir del número de cultivos que hay en el recinto.
     * Al primero se le asignará la letra 'A', al segundo la 'B', y así sucesivamente.
     * @returns string
     */
    private CalcularIdentificadorCultivo(): string {
        if (!this.recintoCultivos?.TwCultivoCollection) {
            return '';
        }
        if (this.recintoCultivos.TwCultivoCollection.length > this.caracteresParaIdentificadorCultivo.length) {
            this.formGroup.disable();
            throw new Error('No hay identificadores de cultivo disponibles. Contacte con el CAU');
        }
        return this.caracteresParaIdentificadorCultivo[this.recintoCultivos?.TwCultivoCollection?.length || 0];
    }

    getIdRecinto(): number {
        return this.cultivo?.IdRecinto || this.recintoCultivos!.Id!;
    }

    onBack(): void {
        this.router.navigate([this.listUrl, this.getIdRecinto()]);
    }

    get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }
}
